import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useUIStore } from '../../stores/uiStore';
import { ThemeToggleCompact } from '../../components/common/theme-toggle';
import { Card, CardContent, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { AlertCircle, Filter, Search, ChevronLeft, ChevronRight, X, User as UserIcon, Mail, Shield, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { getAuditLogs, getUser } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import type { AuditLog, AuditLogResponse, User } from '../../types';
import { formatDateTime } from '../../lib/utils';

// Event type labels for display
const EVENT_TYPE_LABELS: Record<string, string> = {
  login_success: 'Login Success',
  login_failure: 'Login Failure',
  logout: 'Logout',
  password_reset: 'Password Reset',
  account_locked: 'Account Locked',
  account_unlocked: 'Account Unlocked',
};

// Event type colors
const EVENT_TYPE_COLORS: Record<string, string> = {
  login_success: 'bg-green-600',
  login_failure: 'bg-red-600',
  logout: 'bg-gray-600',
  password_reset: 'bg-blue-600',
  account_locked: 'bg-orange-600',
  account_unlocked: 'bg-purple-600',
};

export function AuditLogs() {
  const { isSuperAdmin } = useAuth();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize dark mode
  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  // User details modal
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState<string | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);

  // Filter states
  const [userIdFilter, setUserIdFilter] = useState('');
  const [orgIdFilter, setOrgIdFilter] = useState('');
  const [clientIdFilter, setClientIdFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');

  // Pagination states
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false,
  });

  // Available event types for filter dropdown
  const eventTypes = [
    'login_success',
    'login_failure',
    'logout',
    'password_reset',
    'account_locked',
    'account_unlocked',
  ];

  const fetchAuditLogs = useCallback(async (page: number = 1) => {
    if (!isSuperAdmin()) return;

    setLoading(true);
    setError(null);

    try {
      // Build filters object
      const filters: any = {
        page,
        limit: 100, // Increased from 50 to fit more logs
      };

      if (userIdFilter.trim()) filters.user_ids = userIdFilter.trim();
      if (orgIdFilter.trim()) filters.organization_ids = orgIdFilter.trim();
      if (clientIdFilter.trim()) filters.client_ids = clientIdFilter.trim();
      if (eventTypeFilter) filters.event_types = eventTypeFilter;
      if (startDateFilter) filters.start_date = startDateFilter;
      if (endDateFilter) filters.end_date = endDateFilter;

      const response = await getAuditLogs(filters) as AuditLogResponse;
      setLogs(response.logs || []);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch audit logs');
      console.error('Fetch audit logs error:', err);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, userIdFilter, orgIdFilter, clientIdFilter, eventTypeFilter, startDateFilter, endDateFilter]);

  // Initial load and filter changes
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchAuditLogs(1);
    }, 500); // Debounce

    return () => clearTimeout(handler);
  }, [fetchAuditLogs]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.total_pages) {
      fetchAuditLogs(page);
    }
  };

  const clearFilters = () => {
    setUserIdFilter('');
    setOrgIdFilter('');
    setClientIdFilter('');
    setEventTypeFilter('');
    setStartDateFilter('');
    setEndDateFilter('');
  };

  const fetchUserDetails = async (userId: string) => {
    setUserDetailsLoading(true);
    setUserDetailsError(null);
    setSelectedUser(null);
    setShowUserModal(true);

    try {
      const user = await getUser(userId) as User;
      setSelectedUser(user);
    } catch (err: any) {
      setUserDetailsError(err.message || 'Failed to fetch user details');
    } finally {
      setUserDetailsLoading(false);
    }
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setSelectedUser(null);
    setUserDetailsError(null);
  };

  if (!isSuperAdmin()) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <Button variant="ghost" onClick={() => window.history.back()}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <ThemeToggleCompact />
        </div>
        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              Only super administrators can view audit logs.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header - Compact */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Audit Logs</h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5">System activity and security events</p>
          </div>
        </div>
        <ThemeToggleCompact />
      </div>

      {/* Filters - Compact */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
          <Button variant="outline" size="sm" onClick={clearFilters}>
            Clear
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
          <Input
            placeholder="User ID"
            value={userIdFilter}
            onChange={(e) => setUserIdFilter(e.target.value)}
            className="h-8"
          />
          <Input
            placeholder="Org ID"
            value={orgIdFilter}
            onChange={(e) => setOrgIdFilter(e.target.value)}
            className="h-8"
          />
          <Input
            placeholder="Client ID"
            value={clientIdFilter}
            onChange={(e) => setClientIdFilter(e.target.value)}
            className="h-8"
          />
          <Select value={eventTypeFilter || 'ALL'} onValueChange={(val) => setEventTypeFilter(val === 'ALL' ? '' : val)}>
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Event Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Events</SelectItem>
              {eventTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {EVENT_TYPE_LABELS[type] || type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="date"
            value={startDateFilter}
            onChange={(e) => setStartDateFilter(e.target.value)}
            className="h-8"
          />
          <Input
            type="date"
            value={endDateFilter}
            onChange={(e) => setEndDateFilter(e.target.value)}
            className="h-8"
          />
        </div>
      </Card>

      {/* Status Bar */}
      {(loading || error || logs.length > 0) && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg border dark:border-gray-700">
          {loading && <span>Loading...</span>}
          {error && <span className="text-red-600">Error: {error}</span>}
          {!loading && !error && logs.length > 0 && (
            <>
              <span>Found {pagination.total} log{pagination.total !== 1 ? 's' : ''}</span>
              <span>Page {pagination.page} of {pagination.total_pages}</span>
            </>
          )}
          {!loading && !error && logs.length === 0 && (
            <span>No logs found</span>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-8">
          <LoadingSpinner size={32} />
        </div>
      )}

      {/* Empty State */}
      {!loading && logs.length === 0 && !error && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8 text-center">
            <Search className="h-8 w-8 text-gray-400 mb-2" />
            <p className="text-gray-600 dark:text-gray-400 text-sm">No audit logs found</p>
          </CardContent>
        </Card>
      )}

      {/* Logs List - Compact Table */}
      {!loading && logs.length > 0 && (
        <div className="space-y-2">
          {logs.map((log) => (
            <details key={log.id} className="group">
              <summary className="cursor-pointer list-none">
                <Card className="hover:shadow-md transition-shadow">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between p-3 gap-2">
                    {/* Left: Event + Time */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Badge className={`${EVENT_TYPE_COLORS[log.event_type] || 'bg-gray-600'} text-xs`}>
                        {EVENT_TYPE_LABELS[log.event_type] || log.event_type}
                      </Badge>
                      <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatDateTime(log.created_at)}
                      </span>
                    </div>

                    {/* Middle: IDs (expanded to show full user ID) */}
                    <div className="flex items-center gap-3 text-xs text-gray-600 dark:text-gray-400 flex-1 min-w-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); fetchUserDetails(log.user_id); }}
                        className="font-mono hover:text-blue-600 hover:underline text-left"
                        title="Click to view user details"
                      >
                        User: <code className="bg-gray-100 dark:bg-gray-800 dark:text-gray-200 px-1.5 py-0.5 rounded">{log.user_id}</code>
                      </button>
                      <span className="font-mono" title={log.ip_address}>
                        IP: <code className="bg-gray-100 dark:bg-gray-800 dark:text-gray-200 px-1.5 py-0.5 rounded">{log.ip_address}</code>
                      </span>
                    </div>

                    {/* Right: Expand indicator */}
                    <div className="text-xs text-gray-400 dark:text-gray-500">
                      {log.details || log.user_agent ? '▼' : ''}
                    </div>
                  </div>
                </Card>
              </summary>

              {/* Expanded Details */}
              {(log.details || log.user_agent) && (
                <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-800/50 rounded border dark:border-gray-700 space-y-2">
                  {/* Details */}
                  {log.details && (
                    <div>
                      <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Details:</div>
                      <pre className="text-xs font-mono bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-2 rounded overflow-x-auto whitespace-pre-wrap border dark:border-gray-700">
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* User Agent */}
                  {log.user_agent && (
                    <div>
                      <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">User Agent:</div>
                      <div className="text-xs font-mono bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-2 rounded break-all border dark:border-gray-700">
                        {log.user_agent}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </details>
          ))}
        </div>
      )}

      {/* Pagination - Compact */}
      {!loading && logs.length > 0 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!pagination.has_prev}
            onClick={() => handlePageChange(pagination.page - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
            Prev
          </Button>

          <span className="text-sm text-gray-600 dark:text-gray-400">
            {pagination.page} / {pagination.total_pages}
          </span>

          <Button
            variant="outline"
            size="sm"
            disabled={!pagination.has_next}
            onClick={() => handlePageChange(pagination.page + 1)}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* User Details Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeUserModal}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
              <div className="flex items-center gap-2">
                <UserIcon className="h-5 w-5 text-blue-600" />
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">User Details</h3>
              </div>
              <button onClick={closeUserModal} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              {userDetailsLoading && (
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <LoadingSpinner size={24} />
                  <p className="text-sm text-gray-600 dark:text-gray-400">Loading user details...</p>
                </div>
              )}

              {userDetailsError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{userDetailsError}</AlertDescription>
                </Alert>
              )}

              {selectedUser && !userDetailsLoading && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">User ID</span>
                    <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 dark:text-gray-200 px-2 py-1 rounded">{selectedUser.id}</code>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Name</span>
                    <span className="text-sm font-medium flex items-center gap-1">
                      <UserIcon className="h-3 w-3 text-gray-400" />
                      {selectedUser.name}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Email</span>
                    <span className="text-sm font-medium flex items-center gap-1">
                      <Mail className="h-3 w-3 text-gray-400" />
                      {selectedUser.email}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Role</span>
                    <span className="text-sm font-medium flex items-center gap-1">
                      <Shield className="h-3 w-3 text-gray-400" />
                      {selectedUser.role.replace('_', ' ')}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Email Verified</span>
                    <span className="text-sm font-medium flex items-center gap-1">
                      {selectedUser.email_verified ? (
                        <CheckCircle className="h-3 w-3 text-green-600" />
                      ) : (
                        <XCircle className="h-3 w-3 text-red-600" />
                      )}
                      {selectedUser.email_verified ? 'Yes' : 'No'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Created</span>
                    <span className="text-sm font-medium">
                      {formatDateTime(selectedUser.created_at)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Updated</span>
                    <span className="text-sm font-medium">
                      {formatDateTime(selectedUser.updated_at)}
                    </span>
                  </div>

                  {selectedUser.org_id && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Organization</span>
                      <code className="text-xs font-mono bg-gray-100 dark:bg-gray-800 dark:text-gray-200 px-2 py-1 rounded">{selectedUser.org_id}</code>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex justify-end">
              <Button onClick={closeUserModal} size="sm">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
